import { emitExportTelemetry } from './telemetry'
import type { ExportColumn, SheetInput, XlsxOptions } from './types'

const ILLEGAL_SHEET_CHARACTERS = /[\\/:?*[\]]/gu
const MAX_SHEET_NAME_LENGTH = 31
const MAX_FILENAME_LENGTH = 30

function stripControlCharacters(value: string): string {
  return [...value]
    .filter((character) => {
      const code = character.codePointAt(0) ?? 0
      return code >= 32 && code !== 127
    })
    .join('')
}

function normalizedBaseName(value: string, fallback: string): string {
  const sanitized = stripControlCharacters(value)
    .replace(/[<>:"/\\|?*]/gu, '-')
    .trim()
    .replace(/[. ]+$/u, '')
  return sanitized || fallback
}

export function getSafeXlsxFilename(value: string): string {
  const withoutExtension = value.replace(/\.xlsx$/iu, '')
  return `${normalizedBaseName(withoutExtension, 'export').slice(0, MAX_FILENAME_LENGTH)}.xlsx`
}

function baseSheetName(value: string): string {
  return (
    stripControlCharacters(value)
      .replace(ILLEGAL_SHEET_CHARACTERS, '')
      .trim()
      .replace(/^'+|'+$/gu, '') || 'Sheet'
  )
}

/**
 * Legacy `format.js:1382-1400`: strip Excel's illegal characters, clamp to 31, de-dupe with
 * `_1`/`_2`… The occupied set is compared case-insensitively because Excel itself treats two
 * worksheet names differing only in case as the same name.
 */
export function getSafeWorksheetName(value: string, occupied: ReadonlySet<string>): string {
  const base = baseSheetName(value).slice(0, MAX_SHEET_NAME_LENGTH)
  let candidate = base
  let suffix = 1
  const keys = new Set([...occupied].map((item) => item.toLowerCase()))
  while (keys.has(candidate.toLowerCase())) {
    const marker = `_${suffix}`
    candidate = `${base.slice(0, MAX_SHEET_NAME_LENGTH - marker.length)}${marker}`
    suffix += 1
  }
  return candidate
}

function escapedSuffix(value: string): string {
  return value.replace(/"/gu, '""')
}

/**
 * Module 08's numeric column vocabulary (`reportColumnKind.ts`). Legacy listed `numeric`/`meta`
 * only and leaned on the `numeric` flag to catch `decimal`; covering all three keeps a v4
 * `decimal` column formatted even when the caller omits that flag.
 */
const NUMERIC_SUB_TYPES = new Set(['decimal', 'meta', 'numeric'])

/**
 * Excel number format for **one cell**, ported from `format.js:1360-1380`. The fraction-vs-integer
 * variant is chosen per cell value, so a whole number prints without decimals; with no value the
 * fraction variant wins, matching legacy. `null` means the column carries no numeric intent and the
 * caller must leave `numFmt` untouched. The currency suffix is used verbatim — the caller owns its
 * spacing (e.g. `' USD'`) — with `"` doubled so it cannot break out of the format string.
 */
export function getExcelNumberFormat(
  column: ExportColumn,
  currencySuffix = '',
  value: unknown = null,
): string | null {
  const hasFraction = typeof value === 'number' ? !Number.isInteger(value) : true

  if (column.percentage || column.sub_type === 'percentage') {
    return hasFraction ? '#,##0.####%' : '#,##0%'
  }
  if (column.currency || column.sub_type === 'currency') {
    const suffix = escapedSuffix(currencySuffix)
    const pattern = hasFraction ? '#,##0.##' : '#,##0'
    return suffix ? `${pattern}"${suffix}"` : pattern
  }
  if (column.numeric || NUMERIC_SUB_TYPES.has(column.sub_type ?? '')) {
    return hasFraction ? '#,##0.####' : '#,##0'
  }
  return null
}

function excelValue(value: unknown): string | number | boolean | Date | null {
  if (value === null || value === undefined) return null
  if (typeof value === 'number') return Number.isFinite(value) ? value : null
  if (value instanceof Date) return Number.isFinite(value.getTime()) ? value : null
  if (typeof value === 'string' || typeof value === 'boolean') return value
  try {
    return JSON.stringify(value)
  } catch {
    return String(value)
  }
}

/** Pure, lazy XLSX writer. No vendor code enters the initial application bundle. */
export async function exportXlsx(
  sheets: readonly SheetInput[],
  filename: string,
  options: XlsxOptions = {},
): Promise<void> {
  options.onProgress?.({ phase: 'preparing', pct: 0 })
  try {
    const [excelModule, saverModule] = await Promise.all([import('exceljs'), import('file-saver')])
    const workbook = new excelModule.Workbook()
    const occupied = new Set<string>()

    for (const sheet of sheets) {
      const name = getSafeWorksheetName(sheet.name, occupied)
      occupied.add(name)
      const worksheet = workbook.addWorksheet(name)
      worksheet.addRow(sheet.columns.map((column) => column.header))

      for (const row of sheet.data) {
        const values = sheet.columns.map((column) => excelValue(row[column.field]))
        const excelRow = worksheet.addRow(values)
        for (const [index, column] of sheet.columns.entries()) {
          const value = values[index]
          if (typeof value !== 'number') continue
          const numberFormat = getExcelNumberFormat(column, options.currencySuffix, value)
          if (numberFormat) excelRow.getCell(index + 1).numFmt = numberFormat
        }
      }
      worksheet.columns.forEach((column, index) => {
        const headerLength = sheet.columns[index]?.header.length ?? 0
        column.width = Math.min(60, Math.max(10, headerLength + 2))
      })
    }

    options.onProgress?.({ phase: 'writing', pct: 75 })
    const buffer = await workbook.xlsx.writeBuffer()
    const bytes = new Uint8Array(buffer as ArrayBuffer)
    saverModule.saveAs(
      new Blob([bytes], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      }),
      getSafeXlsxFilename(filename),
    )
    emitExportTelemetry(options.telemetry, options.onTelemetry)
    options.onProgress?.({ phase: 'done', pct: 100 })
  } catch (cause) {
    options.onProgress?.({ phase: 'error' })
    throw cause
  }
}
